import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Client Information Change → Banking / ACH Instructions Update.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Banking / ACH Instructions Update
 * Screen" (region "Banking / ACH Instructions Update Details" with the nested
 * "Bank / ACH Instructions" section) and "Select Banking / ACH Instructions
 * Account Screen" (the account table, notice and Acknowledgement). The account
 * table is fed by Get_Banking_ACH_Registration_Records. The other two Client
 * Information Change sub-categories use different screens and have their own
 * guides. Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Client Information Change',
    subCategory: 'Banking / ACH Instructions Update',
    subject: subjectRow('ACH instructions update - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        Address / Contact Update and Investment Objective / Risk Update are different forms with their own
        guides - this one covers Banking / ACH Instructions Update only.
      </>
    ),
  }),
  {
    title: 'Describe the change',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Banking / ACH Instructions Update Details</strong> screen asks, one at a time, whether you are
        adding a new bank account, whether you are adding, removing or replacing instructions, which direction
        money may move, and whether any account involves another owner. Then fill in the{' '}
        <em>Additional Instructions</em> box - list any extra owners there, and anything else Operations needs.
      </p>
    ),
  },
  {
    title: 'Enter the bank details (new or replacement bank only)',
    body: (
      <p style={{ margin: 0 }}>
        If you are adding a new bank account or replacing existing instructions, a <strong>Bank / ACH
        Instructions</strong> section opens under the instructions box: bank name, account type, the owner names
        on the bank account, account and routing numbers, and the bank&rsquo;s address. It ends with{' '}
        <em>Do you have a voided check?</em> - answer Yes and upload tiles appear for the voided check or other
        bank documents.
      </p>
    ),
  },
  {
    title: 'Pick the accounts and submit',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Click Next. On <strong>Select Banking / ACH Instructions Accounts</strong>, the table{' '}
          <em>Select Accounts for Banking / ACH Instructions</em> lists the household&rsquo;s accounts with Account
          Number, Account Details, Total Cash and Total Value. Tick every account the instructions should be added
          to, updated on or removed from - you can tick several.
        </p>
        <p style={{ margin: 0 }}>
          Read the Operations and Trading notice under the table, select <em>I have read, understand, and agree to
          the above information</em> under Acknowledgement and click Next. FSC creates the case, attaches any files
          and opens it. Note the case number for follow-up.
        </p>
      </>
    ),
  },
];

// Every question on the Banking / ACH Instructions Update screens, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'Banking / ACH Instructions Update Details',
    q: 'Do you need to add a new Bank Account?',
    when: 'Always - this is the first question',
    options: ['Yes (I am adding a new Bank Account)', 'No (I do not need to add a new Bank Account)'],
  },
  {
    q: 'Does this change involve adding new instructions, removing existing, or replacing existing?',
    when: 'Once the first question is answered',
    options: [
      'Replacing Existing Banking/ACH Instructions/Authorization',
      'Removing Banking/ACH Instructions/Authorization',
      'Adding New Banking/ACH Instructions/Authorization',
    ],
    note: 'Removing skips the direction question and goes straight to the account types question.',
  },
  {
    q: 'Which Direction(s) should funds be allowed to move?',
    when: 'Only if you are replacing or adding instructions',
    options: [
      'Setup ACH In (From Bank Account TO Account with Prosperity)',
      'Setup ACH OUT (From Prosperity Account TO Bank Account)',
      'Setup ACH IN and OUT (To/From Prosperity, To/From Bank Account)',
    ],
    note: 'Pick one - choose IN and OUT if money should move both ways.',
  },
  {
    q: 'Are you adding instructions to any of the following accounts types?',
    when: 'Once a direction is chosen, or straight after choosing Removing',
    options: [
      'Trust Account (One Trustee / Owner)',
      'Trust Account (MORE than One Owner / Trustee - Will need all Trustees to Sign)',
      'Joint Account / POA Account',
      'Business Account (Sole Owner)',
      'Business Account (Multiple Owners)',
      'Other (Another Account Type that involves some other Person on the account)',
      'None of the Above / None of the Accounts will involve another Owner',
    ],
    note: 'Any answer other than None of the Above adds joint owner name fields to the bank details below.',
  },
  {
    q: 'Additional Instructions (If there are more owners, please list below, otherwise, please add as much information as possible for the Operations Team)',
    when: 'Once the account types question is answered',
    note: 'Required, under 1,500 characters. List every additional owner or trustee here. The bank details section waits until this has text.',
  },
  {
    screen: 'Bank / ACH Instructions',
    q: 'Name of Banking Institution',
    when: 'Only if you are adding a new bank account or replacing instructions, once Additional Instructions has text',
  },
  {
    q: 'Bank Account Type (Joint, Individual, TOD, etc. etc.)',
    when: 'In the bank details section',
  },
  {
    q: 'First Name (Bank Account Owner / Name on Account) · Last Name (Bank Account Owner / Name on Account)',
    when: 'In the bank details section',
    note: 'The name exactly as it appears on the bank account.',
  },
  {
    q: 'Joint First Name (Bank Account Owner / Name on Account) · Joint Last Name (Bank Account Owner / Name on Account)',
    when: 'In the bank details section, unless you chose None of the Above / None of the Accounts will involve another Owner',
    note: 'The second owner on the bank account. If there are more than two, list the rest in Additional Instructions.',
  },
  {
    q: 'Bank Account Number · Bank Account Routing Number',
    when: 'In the bank details section',
  },
  {
    q: 'Bank address',
    when: 'In the bank details section',
    note: 'A standard address block for the bank. Required.',
  },
  {
    q: 'Do you have a voided check?',
    when: 'In the bank details section',
    options: ['Yes (I have a voided check or other relevant documents)', 'No (I do not have a voided check)'],
    note: 'Yes opens upload tiles for the voided check or other bank documents. The screen warns that missing or incomplete documents may result in a NIGO.',
  },
  {
    screen: 'Select Banking / ACH Instructions Accounts',
    q: 'Select Accounts for Banking / ACH Instructions',
    when: 'Always, on the second screen',
    note: 'A table with Account Number, Account Details, Total Cash and Total Value. Tick every account the change applies to - you can pick more than one.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Client Information Change'),
  {
    h: "The bank details section hasn't appeared",
    p: [
      'It only opens if you are adding a new bank account or replacing existing instructions, and only once the Additional Instructions box has something in it.',
    ],
  },
  {
    h: "The account I expected isn't in the list",
    p: [
      'The table lists the household’s active accounts - one row per account, not per sleeve. Inactive accounts are left out. If an account that should be there is missing, the screen asks you to reach out to the Operations Team for support.',
    ],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function ClientInfoBankingAch() {
  return (
    <GuidePage
      title="Banking and ACH Instruction Updates"
      icon="bank"
      path="Operations → Client Information Change → Banking / ACH Instructions Update"
      minutes={5}
      intro="How to add, replace or remove a client's banking and ACH instructions with the Prosperity Operations team - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The bank details are only asked for when you add a new bank or replace instructions, but then every field is required.</Para>
        <Bullets items={[
          <><strong>Which accounts</strong> the instructions apply to. You pick them from a list, so you do not need account numbers memorised.</>,
          <><strong>Whether money moves in, out or both ways</strong> between the bank and the Prosperity accounts.</>,
          <><strong>Every owner and trustee</strong> on trust, joint, POA and business accounts. For a trust with more than one trustee, all trustees will need to sign.</>,
          <><strong>The bank details</strong> for a new or replacement bank: bank name and address, account type, the names on the bank account, account number and routing number.</>,
          <><strong>A voided check</strong> or other bank document, ready to upload. Missing or incomplete documents may result in a NIGO.</>,
        ]} />
        <Callout>
          One case covers one bank. If you are setting up two different bank accounts, submit a separate case for
          each.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="operations"
        before={(
          <Para>
            Your answers and the accounts you ticked are written onto the case. When you add a new bank or replace
            instructions, FSC also creates one banking record per account you ticked, carrying the bank details and
            your instructions.
          </Para>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe the custodian&rsquo;s own forms or
        verification steps for a bank link.
      </Footnote>
    </GuidePage>
  );
}
